import type { ContentItemViewNodesType } from 'components/WorkspaceEditor/types/contentItemViewNode.type'
import { toPascalCase } from 'components/WorkspaceEditor/utils'
import {
    InputBuilder,
    InputBuilderProps,
    TextContentElementMutation,
} from './InputBuilder'
import { ObjectBuilder } from './ObjectBuilder'

/** Input parameters for the mutation to update the content item element. */
interface SetProjectContentItemElementsInput {
    projectProductFamilyId: string
    contentItemId: string
    contentItemOwnerType: string
    elements: TextContentElementMutation[]
    projectId: string
    contentItemOwnerId: string
    nodeId: string
    textType: string
    partType: string
    documentTemplateViewId: string
    documentTemplateViewItemId: string
    contentItemParentId: string
    contentItemParentType: string
    propertyKey?: string
    isFromStudioManagementModule: boolean
    // unique for Specifier notes mutation input
    specifierNotes: string[]
    updateType: 'SpecifierNote' | 'ArticleSpecifierNote'
}

/** Variables for the mutation to update the content item element. */
interface SetProjectContentItemElementsProps {
    input: SetProjectContentItemElementsInput
    partType: string
    projectId: string
    rootArticleContentItemIds: string[]
    productFamilyId: string
}

/** Combined props for the constructor */
interface SpecifierNotesInputBuilderProps extends InputBuilderProps {
    specifierNotes: string[]
    isForArticle?: boolean
}

/**
 * A Class that encapsulates helper functions for building input variables
 * for updating project content item elements.
 *
 * @param {SpecifierNotesInputBuilderProps} props - The properties for the builder.
 * @param {string} props.contentItemId - The ID of the content item.
 * @param {string[]} props.specifierNotes - The specifier notes to be updated.
 * @param {string} props.selectedProjectElementNodeId - The ID of the selected project element node.
 * @param {string} props.elementId - The ID of the element.
 * @param {ContentItemViewNodesType[]} props.contentItemViewNodes - The view nodes of the content item.
 */
export class SpecifierNotesInputBuilder extends InputBuilder<
    SetProjectContentItemElementsInput,
    SetProjectContentItemElementsProps,
    string[] // TData is string[] for specifierNotes
> {
    constructor({
        contentItemId,
        specifierNotes,
        selectedProjectElementNodeId,
        elementId,
        contentItemViewNodes,
        projectId,
        isForArticle,
    }: SpecifierNotesInputBuilderProps) {
        super(
            {
                contentItemId,
                selectedProjectElementNodeId,
                elementId,
                contentItemViewNodes,
                projectId,
                isForArticle,
            },
            // pass specifierNotes as the specific data (TData)
            specifierNotes
        )
        this.contentItem = this.getContentItem(contentItemId)
    }

    /**
     * Retrieves the content item node from global store by its ID.
     * For SpecifierNotes, the contentItemId directly matches the node ID.
     * @param contentItemId - The ID of the content item.
     * @returns The view node containing the content item.
     */
    protected getContentItem = (contentItemId: string) => {
        const contentItem = this.contentItemViewNodes.find(
            (node) => node.id === contentItemId
        ) as ContentItemViewNodesType

        // use projectId from url if not already set
        if (contentItem && !contentItem.projectId) {
            contentItem.projectId = this.projectId || ''
        }

        return contentItem
    }

    /**
     * Retrieves the root article node from content item view nodes.
     * For SpecifierNotes, the current contentItem is considered the parent/root context.
     * @param _node - The node to start the search from (ignored in this implementation). Prefix with _ to avoid lint error.
     * @returns The root article node (which is the current contentItem).
     */
    protected getRootArticle = (
        _node: Partial<ContentItemViewNodesType> | undefined
    ): Partial<ContentItemViewNodesType> | undefined => {
        // For SpecifierNotes, the contentItem itself acts as the relevant root context
        // for determining rootArticleContentItemIds in the final build step.
        return this.contentItem
    }

    /**
     * Builds the elements array for the mutation.
     * For SpecifierNotes, it maps the existing elements without replacement.
     * @returns An array of TextContentElementMutation.
     */
    protected buildElements = (): TextContentElementMutation[] => {
        // SpecifierNotes update doesn't replace elements in the same way ContentItem does.
        // It primarily sends the `specifierNotes` array in the input.
        // We still need to map the *existing* elements of the contentItem to the mutation model.
        return (this.contentItem?.elements || []).map(
            this.toMutationModel.bind(this)
        )
    }

    /**
     * Maps the content item and specifier notes into a Builder instance
     * containing the input for the updateProjectContentItemElements mutation.
     *
     * @param builder - A Builder for SetProjectContentItemElementsInput.
     * @returns The builder with the input set.
     */
    protected buildInput = (
        builder: ObjectBuilder<SetProjectContentItemElementsInput>
    ): ObjectBuilder<SetProjectContentItemElementsInput> => {
        return builder
            .resolveWith(this.nullOrUndefinedResolver)
            .set('projectProductFamilyId', this.elementId)
            .set('contentItemId', this.contentItem?.id)
            .set('contentItemOwnerType', toPascalCase(this.contentItem?.type))
            .set('elements', this.buildElements())
            .set('projectId', this.contentItem?.projectId)
            .set(
                'contentItemOwnerId',
                this.contentItem?.propertyRefId || this.contentItem?.dtvItemId
            )
            .set('nodeId', this.selectedProjectElementNodeId)
            .set('textType', this.contentItem?.textType)
            .set(
                'partType',
                this.contentItem?.partType?.toLocaleUpperCase() || ''
            )
            .set(
                'documentTemplateViewId',
                this.contentItem?.documentTemplateViewId
            )
            .set('documentTemplateViewItemId', this.contentItem?.dtvItemId)
            .set(
                'contentItemParentId',
                this.contentItem?.dtvContentItemParentId
            ) // TODO: change in API: dtvContentItemParentId not "contentItemParentId"
            .set(
                'contentItemParentType',
                this.isForArticle ? 'None' : 'ContentItem'
            )
            .set('isFromStudioManagementModule', true) // specifierNotes can only be updated from Studio Management Module/OM
            .set('specifierNotes', this.specificData)
            .set(
                'updateType',
                this.isForArticle ? 'ArticleSpecifierNote' : 'SpecifierNote'
            )
    }

    /**
     * Builds the input variables for the mutation.
     * @returns The variables to be used in mutation.
     */
    build = (): SetProjectContentItemElementsProps => {
        const rootArticle = this.getRootArticle(this.contentItem)

        return new ObjectBuilder<SetProjectContentItemElementsProps>()
            .resolveWith(this.nullOrUndefinedResolver)
            .set('input', this.buildInput.bind(this))
            .set('partType', this.contentItem?.partType?.toLocaleUpperCase())
            .set('projectId', this.contentItem?.projectId)
            .set('rootArticleContentItemIds', [
                rootArticle?.dtvContentItemParentId || '',
            ])
            .set('productFamilyId', this.elementId)
            .build()
    }
}
